import React, { memo } from 'react';
import { TILE_SIZE, FLOOR_HEIGHT } from './constants';
import FloorTile3D from './tiles/FloorTile3D';
import WallTile3D from './tiles/WallTile3D';
import LavaTile3D from './tiles/LavaTile3D';
import StopTile3D from './tiles/StopTile3D';
import TeleporterTile3D from './tiles/TeleporterTile3D';
import PortalTile3D from './tiles/PortalTile3D';
import type { GraphicsQuality } from './GameBoard3D';

export type TileType = 'empty' | 'rock' | 'lava' | 'stop' | 'teleporter' | 'start' | 'end' | 'powerup' | 'obstacle';

interface TileRenderer3DProps {
  type: TileType;
  col: number;
  row: number;
  totalCols: number;
  totalRows: number;
  isCheckerDark?: boolean;
  graphics?: GraphicsQuality;
}

function TileRenderer3DInner({ type, col, row, totalCols, totalRows, isCheckerDark, graphics = 'high' }: TileRenderer3DProps) {
  // Convert grid [col, row] -> Three.js [x, y, z], centered at origin
  const x = (col - (totalCols - 1) / 2) * TILE_SIZE;
  const z = (row - (totalRows - 1) / 2) * TILE_SIZE;
  const floorY = FLOOR_HEIGHT / 2;
  const entranceDelay = (col + row) * 0.02;

  // Always render a floor tile underneath
  const floorPos: [number, number, number] = [x, floorY, z];

  switch (type) {
    case 'rock':
      return (
        <>
          <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />
          <WallTile3D position={[x, 0, z]} entranceDelay={entranceDelay} graphics={graphics} />
        </>
      );
    case 'lava':
      return (
        <>
          <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />
          <LavaTile3D position={[x, FLOOR_HEIGHT, z]} entranceDelay={entranceDelay} graphics={graphics} />
        </>
      );
    case 'stop':
      return (
        <>
          <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />
          <StopTile3D position={[x, FLOOR_HEIGHT, z]} entranceDelay={entranceDelay} graphics={graphics} />
        </>
      );
    case 'teleporter':
      return (
        <>
          <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />
          <TeleporterTile3D position={[x, FLOOR_HEIGHT, z]} entranceDelay={entranceDelay} />
        </>
      );
    case 'start':
      return (
        <>
          <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />
          <PortalTile3D position={[x, FLOOR_HEIGHT, z]} variant="start" entranceDelay={entranceDelay} />
        </>
      );
    case 'end':
      return (
        <>
          <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />
          <PortalTile3D position={[x, FLOOR_HEIGHT, z]} variant="end" entranceDelay={entranceDelay} />
        </>
      );
    case 'powerup':
    case 'empty':
    case 'obstacle':
    default:
      return <FloorTile3D position={floorPos} isCheckerDark={isCheckerDark} entranceDelay={entranceDelay} />;
  }
}

const TileRenderer3D = memo(TileRenderer3DInner);
export default TileRenderer3D;
